import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsTradingCompany } from "@shared-core/data/api-types";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import { MonthlyRevenueTab } from "../app/components/domain/insights/MonthlyRevenueTab";
import { TopCustomersTab } from "../app/components/domain/insights/TopCustomersTab";
import { WallboardReconciliationTab } from "../app/components/domain/insights/WallboardReconciliationTab";
import { DailyRevenueTab } from "../app/components/domain/insights/DailyRevenueTab";
import { CampaignImpactTab } from "../app/components/domain/insights/CampaignImpactTab";
import { RevenueBridgeTab } from "../app/components/domain/insights/RevenueBridgeTab";
import { SalesMixTab } from "../app/components/domain/insights/SalesMixTab";
import { MonthLandingTab } from "../app/components/domain/insights/MonthLandingTab";
import { CarriageRecoveryTab } from "../app/components/domain/insights/CarriageRecoveryTab";
import { ProductUsageTab } from "../app/components/domain/insights/ProductUsageTab";
import { TicketFollowthroughTab } from "../app/components/domain/insights/TicketFollowthroughTab";
import { AlertFeedbackTab } from "../app/components/domain/insights/AlertFeedbackTab";
import { TradingCompanySelect } from "../app/components/domain/insights/TradingCompanySelect";
import { useCanReadMarketing, useCanViewInsightsTab } from "../app/auth/hooks";

// Native tabbed analytics page — replaces the retired Superset embed. Each
// tab is a composed views_module endpoint rendered with recharts, natively
// over the dbt metrics layer.
//
// `gate` is the permit a tab's endpoint requires beyond the CORE_ALL every
// signed-in user has. `null` means there is nothing beyond it, so the tab
// stays visible to everyone — hiding one here would restrict users the
// backend still serves. Retrofitting the older tabs onto the Insights tab
// families (recorded in
// docs/features/carriage-cost-capture/08_architecture_review.md) is a backend
// change first; each one then swaps `null` for its family here.
const TABS = [
  { value: "monthlanding", label: "Month Landing", gate: null },
  { value: "wallboard", label: "Wallboard Reconciliation", gate: null },
  { value: "top100", label: "Top 100 Customers", gate: null },
  { value: "monthly", label: "Monthly", gate: null },
  { value: "daily", label: "Daily", gate: null },
  { value: "campaignimpact", label: "Campaign Impact", gate: "marketing" },
  { value: "bridge", label: "Revenue Bridge", gate: null },
  { value: "salesmix", label: "Sales Mix", gate: null },
  { value: "carriage", label: "Carriage Recovery", gate: "commercial" },
  { value: "productusage", label: "Product Usage", gate: "management" },
  {
    value: "ticketfollowthrough",
    label: "Ticket Follow-through",
    gate: "management",
  },
  { value: "alertfeedback", label: "Alert Feedback", gate: "management" },
] as const;

const DEFAULT_TAB = "monthly";

// The tabs whose figures the Trading company control narrows. It is shown only
// on these, so it never seems to filter a tab it does not.
const TRADING_COMPANY_TABS: readonly string[] = ["top100", "monthly", "daily"];

// The options the control offers, loaded once per page. Until they load the
// URL's code is trusted (the server validates it); if they fail to load the
// control is absent and the tabs show the group.
type TradingCompanyOptions =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; tradingCompanies: InsightsTradingCompany[] };

function useTradingCompanyOptions(): TradingCompanyOptions {
  const adapter = useDataAdapter();
  const [options, setOptions] = useState<TradingCompanyOptions>({
    status: "loading",
  });
  useEffect(() => {
    let cancelled = false;
    adapter
      .getInsightsTradingCompanies()
      .then((result) => {
        if (!cancelled) {
          setOptions({
            status: "ready",
            tradingCompanies: result.trading_companies,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setOptions({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);
  return options;
}

export function InsightsPage() {
  // One gate per tab, resolved once. The backend permit on each endpoint is
  // the real gate; hiding a tab keeps the page honest for the roles that
  // would otherwise meet a 403 rendered as "couldn't load", which reads as an
  // outage rather than as "not for you". `marketing` is MARKETING_ENGINE_READ
  // (Campaign Impact carries campaign names); the rest are Insights tab
  // families.
  const allowed = {
    marketing: useCanReadMarketing(),
    commercial: useCanViewInsightsTab("commercial"),
    management: useCanViewInsightsTab("management"),
    finance: useCanViewInsightsTab("finance"),
  };
  const canView = (gate: (typeof TABS)[number]["gate"]) =>
    gate === null || allowed[gate];
  const tabs = TABS.filter((t) => canView(t.gate));

  // URL-driven so other pages can deep-link to a specific tab (e.g. Marketing
  // Results → Insights?tab=campaignimpact), not just this page's own clicks.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = tabs.some((t) => t.value === requestedTab)
    ? requestedTab!
    : DEFAULT_TAB;

  // ?trading_company= sits beside ?tab=, so the choice survives switching
  // between the tabs that take it. A code the options do not list is read as
  // the group rather than sent to be refused.
  const tradingCompanyOptions = useTradingCompanyOptions();
  const requestedTradingCompany =
    searchParams.get("trading_company") ?? undefined;
  const tradingCompany =
    tradingCompanyOptions.status === "error"
      ? undefined
      : tradingCompanyOptions.status === "ready" &&
          !tradingCompanyOptions.tradingCompanies.some(
            (c) => c.code === requestedTradingCompany,
          )
        ? undefined
        : requestedTradingCompany;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground mb-2">Insights</h1>
          <p className="text-muted-foreground">
            Business intelligence and analytics over live production data
          </p>
        </div>
        {TRADING_COMPANY_TABS.includes(tab) &&
          tradingCompanyOptions.status === "ready" && (
            <TradingCompanySelect
              tradingCompanies={tradingCompanyOptions.tradingCompanies}
              value={tradingCompany}
              onChange={(next) =>
                setSearchParams(
                  (prev) => {
                    const params = new URLSearchParams(prev);
                    if (next === undefined) params.delete("trading_company");
                    else params.set("trading_company", next);
                    return params;
                  },
                  { replace: true },
                )
              }
            />
          )}
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (value === DEFAULT_TAB) next.delete("tab");
              else next.set("tab", value);
              return next;
            },
            { replace: true },
          )
        }
        className="gap-6"
      >
        <TabsList variant="underline">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="monthlanding">
          <MonthLandingTab />
        </TabsContent>
        <TabsContent value="wallboard">
          <WallboardReconciliationTab />
        </TabsContent>
        <TabsContent value="top100">
          <TopCustomersTab tradingCompany={tradingCompany} />
        </TabsContent>
        <TabsContent value="monthly">
          <MonthlyRevenueTab tradingCompany={tradingCompany} />
        </TabsContent>
        <TabsContent value="daily">
          <DailyRevenueTab tradingCompany={tradingCompany} />
        </TabsContent>
        {canView("marketing") && (
          <TabsContent value="campaignimpact">
            <CampaignImpactTab />
          </TabsContent>
        )}
        <TabsContent value="bridge">
          <RevenueBridgeTab />
        </TabsContent>
        <TabsContent value="salesmix">
          <SalesMixTab />
        </TabsContent>
        {canView("commercial") && (
          <TabsContent value="carriage">
            <CarriageRecoveryTab />
          </TabsContent>
        )}
        {canView("management") && (
          <TabsContent value="productusage">
            <ProductUsageTab />
          </TabsContent>
        )}
        {canView("management") && (
          <TabsContent value="ticketfollowthrough">
            <TicketFollowthroughTab />
          </TabsContent>
        )}
        {canView("management") && (
          <TabsContent value="alertfeedback">
            <AlertFeedbackTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
